/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * Keeps the chosen size when the shopper switches between the products of a
 * gear set: the gear set itself and its component products, linked by
 * snippets/gear-set-variations.liquid.
 *
 * Every size picker carries the product it sizes in data-size-component-id: the
 * product itself on a component page, the matching component product for each
 * of a gear set's pickers (see snippets/variant-size-selector.liquid). A click
 * on a gear-set link adds the sizes picked in the main variant picker to the
 * sizes remembered for this browser tab, by that id. The next product page,
 * reached by soft navigation or by a full load, then picks the remembered size
 * in each picker sizing the same product, the way a shopper's click would. A
 * jacket size picked before visiting the pants is still selected back on the
 * gear set; pickers for a product without a remembered size stay empty.
 *
 * During the switch the root element carries data-gear-set-switch, which tells
 * variant-picker.js to start from empty sizes even though the departing
 * product's ?variant= is still in the address bar (soft navigation swaps the
 * page before it pushes the new URL).
 */

/** Remembered size per sized product id, for the lifetime of the browser tab. */
const SIZES_KEY = 'gear-set-sizes';

/** Time of the gear-set link click; the next page applies the sizes only when set. */
const SWITCH_KEY = 'gear-set-switch';

const SWITCH_ATTRIBUTE = 'data-gear-set-switch';

/** A switch older than this is ignored, e.g. after a failed navigation. */
const MAX_AGE_MS = 30_000;

/** The switch marker is dropped after this long if no new page arrived (e.g. a modified click). */
const SWITCH_TIMEOUT_MS = 10_000;

/** @returns {HTMLElement | undefined} The product page's own variant picker. */
function getMainPicker() {
  return [...document.querySelectorAll('variant-picker[data-template-product-match="true"]')].find(
    (picker) => picker instanceof HTMLElement && !picker.closest('product-card, quick-add-dialog'),
  );
}

/** @param {Element} picker */
function getSizeFieldsets(picker) {
  return /** @type {HTMLFieldSetElement[]} */ ([...picker.querySelectorAll('fieldset[data-size-component-id]')]);
}

/** @returns {Record<string, string>} */
function readSizes() {
  try {
    const sizes = JSON.parse(sessionStorage.getItem(SIZES_KEY) ?? '{}');
    return sizes && typeof sizes === 'object' ? sizes : {};
  } catch {
    return {};
  }
}

function recordSizes() {
  const picker = getMainPicker();
  const sizes = readSizes();

  if (picker) {
    for (const fieldset of getSizeFieldsets(picker)) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      const componentId = fieldset.dataset.sizeComponentId;

      if (checked instanceof HTMLInputElement && componentId) sizes[componentId] = checked.value;
    }
  }

  try {
    sessionStorage.setItem(SIZES_KEY, JSON.stringify(sizes));
    sessionStorage.setItem(SWITCH_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode): the switch simply doesn't carry sizes.
  }

  document.documentElement.setAttribute(SWITCH_ATTRIBUTE, '');
  setTimeout(() => document.documentElement.removeAttribute(SWITCH_ATTRIBUTE), SWITCH_TIMEOUT_MS);
}

/** @returns {boolean} Whether this page was reached by a gear-set switch; clears the marker. */
function takeSwitch() {
  try {
    const switchedAt = Number(sessionStorage.getItem(SWITCH_KEY));
    sessionStorage.removeItem(SWITCH_KEY);
    return switchedAt > 0 && Date.now() - switchedAt <= MAX_AGE_MS;
  } catch {
    return false;
  }
}

async function applySizes() {
  if (!takeSwitch()) {
    document.documentElement.removeAttribute(SWITCH_ATTRIBUTE);
    return;
  }

  const sizes = readSizes();

  // The new picker clears Shopify's default sizes when it connects, which happens
  // when it's defined (full load) or swapped in (soft navigation); pick after that.
  await customElements.whenDefined('variant-picker');
  await new Promise((resolve) => setTimeout(resolve));

  document.documentElement.removeAttribute(SWITCH_ATTRIBUTE);

  const picker = getMainPicker();
  if (!picker) return;

  for (const fieldset of getSizeFieldsets(picker)) {
    const size = sizes[fieldset.dataset.sizeComponentId ?? ''];
    if (!size) continue;

    const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find(
      (input) => input instanceof HTMLInputElement && input.value === size,
    );

    if (radio instanceof HTMLInputElement && !radio.checked) radio.click();
  }
}

if (!(/** @type {any} */ (window).__gearSetSizeCarryInit)) {
  /** @type {any} */ (window).__gearSetSizeCarryInit = true;

  // Capture phase, so the sizes are read before soft navigation replaces the page.
  document.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('a[data-gear-set-navigate]')) recordSizes();
    },
    { capture: true },
  );

  // Soft navigation announces the swapped sections; a full load lands here directly.
  document.addEventListener('shopify:section:load', () => applySizes());
  applySizes();
}
